import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import UserRegistrationModal from '../../components/admin/UserRegistrationModal';
import Modal from '../../components/common/Modal';
import FilterDropdown from '../../components/common/FilterDropdown';

const UserList = () => {
    const { get, del, post } = useApi();
    const { showToast } = useAppState();
    const queryClient = useQueryClient();

    // State for filtering and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [userToInvite, setUserToInvite] = useState(null);
    const [sendingInvite, setSendingInvite] = useState(false);
    const itemsPerPage = 10;

    const { data: users = [], isLoading: loading } = useQuery({
        queryKey: ['admin-users'],
        queryFn: () => get('/api/admin/users/'),
        staleTime: 5 * 60 * 1000,
    });

    const handleEdit = (user) => {
        setSelectedUser(user);
        setIsRegisterModalOpen(true);
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;
        setDeleting(true);
        try {
            await del(`/api/admin/users/${userToDelete.id}/`);
            showToast('User successfully removed from directory', 'success');
            setUserToDelete(null);
            queryClient.invalidateQueries(['admin-users']);
        } catch (err) {
            console.error('Deletion error:', err);
            showToast('Failed to delete user. Please try again.', 'error');
        } finally {
            setDeleting(false);
        }
    };
    const handleInviteClick = (user) => {
        setUserToInvite(user);
    };

    const confirmInvite = async () => {
        if (!userToInvite) return;
        setSendingInvite(true);
        try {
            await post(`/api/admin/users/${userToInvite.id}/send-invite/`, {});
            showToast('Invitation sent successfully!', 'success');
            setUserToInvite(null);
        } catch (err) {
            console.error('Invitation error:', err);
            showToast('Failed to send invitation. Please try again.', 'error');
        } finally {
            setSendingInvite(false);
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch =
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.system_id?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRole = roleFilter === 'all' || user.role === roleFilter;

        const statusValue = user.is_active ? 'active' : 'inactive';
        const matchesStatus = statusFilter === 'all' || statusValue === statusFilter;

        return matchesSearch && matchesRole && matchesStatus;
    });

    // Reset pagination on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, roleFilter, statusFilter]);

    // Pagination Logic
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

    // Generate page numbers for truncated pagination
    const getPageNumbers = () => {
        const pages = [];
        const showMax = 3; // Number of pages to show around current page

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            
            if (currentPage > 3) pages.push('...');
            
            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);
            
            if (currentPage <= 3) {
                start = 2;
                end = 4;
            } else if (currentPage >= totalPages - 2) {
                start = totalPages - 3;
                end = totalPages - 1;
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500 flex flex-col items-center">
                    <i className="fas fa-circle-notch fa-spin text-3xl mb-4 text-[#18216D]"></i>
                    <p className="font-bold uppercase tracking-widest text-xs">Loading Directory...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-black text-[#18216D]">Institutional Directory</h1>
                    <p className="text-slate-400 font-bold text-sm mt-1">Manage all system users, instructors, and student accounts</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => {
                            setSelectedUser(null);
                            setIsRegisterModalOpen(true);
                        }}
                        className="px-6 py-3 bg-[#18216D] text-white rounded-xl hover:bg-[#0D164F] shadow-lg shadow-indigo-900/10 transition-all font-black text-xs uppercase tracking-widest flex items-center space-x-2"
                    >
                        <i className="fas fa-user-plus"></i>
                        <span>Register User</span>
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-900/5 border border-slate-100 p-6">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1 relative">
                        <i className="fas fa-search absolute left-6 top-1/2 -translate-y-1/2 text-slate-300"></i>
                        <input
                            type="text"
                            placeholder="Identify by name, official email, or ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-14 pr-6 py-4 bg-white border-2 border-slate-200 rounded-2xl focus:border-[#FFC425] focus:bg-white transition-all text-sm font-medium text-[#18216D] placeholder-slate-300 outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <FilterDropdown
                            value={roleFilter}
                            onChange={setRoleFilter}
                            options={[
                                { name: 'Every Role', value: 'all' },
                                { name: 'Teachers', value: 'teacher' },
                                { name: 'Students', value: 'student' },
                                { name: 'Guardians', value: 'parent' },
                                { name: 'Administrators', value: 'admin' }
                            ]}
                        />
                        <FilterDropdown
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                { name: 'Any Status', value: 'all' },
                                { name: 'Active', value: 'active' },
                                { name: 'Suspended', value: 'inactive' }
                            ]}
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-900/5 border border-slate-100 overflow-hidden">
                {currentUsers.length === 0 ? (
                    <div className="text-center py-24 text-slate-300">
                        <div className="text-7xl mb-6 opacity-20">
                            <i className="fas fa-users-viewfinder"></i>
                        </div>
                        <p className="text-xl font-black text-[#18216D]">No records found</p>
                        <p className="text-sm font-medium mt-1">Adjust your parameters and search again.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50/50">
                                <tr>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Name & Email</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">System Identifier</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Role</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Level / Linkage</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Account State</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {currentUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-indigo-50/30 transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className={`h-12 w-12 rounded-2xl flex items-center justify-center font-black text-sm shadow-sm transition-transform group-hover:scale-110 ${user.role === 'admin' ? 'bg-amber-100 text-amber-700' :
                                                    user.role === 'teacher' ? 'bg-purple-100 text-purple-700' :
                                                        user.role === 'parent' ? 'bg-emerald-100 text-emerald-700' :
                                                            'bg-indigo-100 text-[#18216D]'
                                                    }`}>
                                                    {user.name?.charAt(0) || user.username?.charAt(0)}
                                                </div>
                                                <div>
                                                    <div className="font-black text-[#18216D] group-hover:text-[#18216D] transition-colors text-sm">{user.name}</div>
                                                    <div className="text-xs font-bold text-slate-400 mt-0.5">{user.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className="text-[10px] font-mono font-black text-[#18216D] bg-indigo-50 px-2.5 py-1.5 rounded-lg border border-indigo-100/50 shadow-sm">
                                                {user.system_id || 'PROVISIONING'}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-2">
                                                <span className={`p-2 rounded-xl text-xs shadow-sm ${user.role === 'admin' ? 'bg-amber-50 text-amber-600' :
                                                    user.role === 'teacher' ? 'bg-purple-50 text-purple-600' :
                                                        user.role === 'parent' ? 'bg-emerald-50 text-emerald-600' :
                                                            'bg-sky-50 text-sky-600'
                                                    }`}>
                                                    <i className={`fas ${user.role === 'admin' ? 'fa-user-shield' :
                                                        user.role === 'teacher' ? 'fa-chalkboard-user' :
                                                            user.role === 'parent' ? 'fa-user-group' :
                                                                'fa-user-graduate'
                                                        } w-4 text-center`}></i>
                                                </span>
                                                <span className="text-[10px] font-black text-[#18216D]/60 uppercase tracking-widest leading-none">{user.role}</span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            {user.role === 'student' ? (
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-black text-[#18216D]">
                                                        {user.grade_level_name ? `${user.grade_level_name}${user.stream_name !== 'UNASSIGNED' ? ` - ${user.stream_name}` : ''}` : 'UNASSIGNED'}
                                                    </span>
                                                    {user.career_pathway && (
                                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                                                            {user.career_pathway} Pathway
                                                        </span>
                                                    )}
                                                </div>
                                            ) : user.role === 'parent' ? (
                                                <span className="text-sm font-black text-[#18216D]">
                                                    {user.children_count || 0} Linked Scholars
                                                </span>
                                            ) : (
                                                <span className="text-sm font-black text-[#18216D]">
                                                    Institutional Faculty
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className={`inline-flex px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-full shadow-sm border ${user.is_active
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                                : 'bg-rose-50 text-rose-700 border-rose-100'
                                                }`}>
                                                {user.is_active ? 'Active Status' : 'Session Suspended'}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6 text-right space-x-2">
                                            {user.role === 'parent' && (
                                                <button
                                                    onClick={() => handleInviteClick(user)}
                                                    className="h-10 w-10 text-slate-400 hover:text-emerald-600 rounded-xl hover:bg-white transition-all shadow-sm hover:shadow-md border border-transparent hover:border-slate-100"
                                                    title="Send Password Setup Invitation"
                                                >
                                                    <i className="fas fa-paper-plane text-xs"></i>
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleEdit(user)}
                                                className="h-10 w-10 text-slate-400 hover:text-[#18216D] rounded-xl hover:bg-white transition-all shadow-sm hover:shadow-md border border-transparent hover:border-slate-100"
                                            >
                                                <i className="fas fa-pen-to-square text-xs"></i>
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(user)}
                                                className="h-10 w-10 text-slate-400 hover:text-rose-600 rounded-xl hover:bg-white transition-all shadow-sm hover:shadow-md border border-transparent hover:border-slate-100"
                                            >
                                                <i className="fas fa-trash-can text-xs"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex flex-col items-center gap-4 py-12">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="h-11 w-11 flex items-center justify-center rounded-2xl bg-white border border-slate-100 text-slate-400 hover:text-[#18216D] hover:border-[#18216D]/20 disabled:opacity-30 disabled:hover:text-slate-400 transition-all shadow-sm active:scale-95"
                        >
                            <i className="fas fa-chevron-left text-xs"></i>
                        </button>

                        <div className="flex items-center gap-1.5 px-2">
                            {getPageNumbers().map((page, index) => (
                                page === '...' ? (
                                    <span key={`dots-${index}`} className="w-10 text-center text-slate-300 font-black text-xs tracking-widest">...</span>
                                ) : (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`h-11 min-w-[2.75rem] px-3 flex items-center justify-center rounded-2xl text-[11px] font-black transition-all shadow-sm border ${currentPage === page
                                            ? 'bg-[#18216D] text-white border-[#18216D] shadow-indigo-900/20'
                                            : 'bg-white border-slate-100 text-slate-400 hover:border-[#18216D]/20 hover:text-[#18216D] active:scale-95'
                                            }`}
                                    >
                                        {page}
                                    </button>
                                )
                            ))}
                        </div>

                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="h-11 w-11 flex items-center justify-center rounded-2xl bg-white border border-slate-100 text-slate-400 hover:text-[#18216D] hover:border-[#18216D]/20 disabled:opacity-30 disabled:hover:text-slate-400 transition-all shadow-sm active:scale-95"
                        >
                            <i className="fas fa-chevron-right text-xs"></i>
                        </button>
                    </div>
                    
                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">
                        Displaying page <span className="text-[#18216D]">{currentPage}</span> of <span className="text-[#18216D]">{totalPages}</span>
                    </p>
                </div>
            )}

            {isRegisterModalOpen && (
                <UserRegistrationModal
                    user={selectedUser}
                    onClose={() => {
                        setIsRegisterModalOpen(false);
                        setSelectedUser(null);
                        queryClient.invalidateQueries(['admin-users']);
                    }}
                />
            )}

            <Modal
                isOpen={!!userToDelete}
                onClose={() => setUserToDelete(null)}
                title="Confirm Account Removal"
                type="danger"
                footer={(
                    <>
                        <button
                            onClick={confirmDelete}
                            disabled={deleting}
                            className="bg-rose-600 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-lg shadow-rose-900/20 hover:bg-rose-700 transition-all disabled:opacity-50"
                        >
                            {deleting ? 'Removing...' : 'Permanently Delete'}
                        </button>
                        <button
                            onClick={() => setUserToDelete(null)}
                            className="text-[#18216D] px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-slate-50 transition-all"
                        >
                            Cancel
                        </button>
                    </>
                )}
            >
                <div className="space-y-4">
                    <p>Are you absolutely sure you want to remove <span className="text-[#18216D] font-black underline underline-offset-4 decoration-rose-500/30">{userToDelete?.name}</span> from the Institutional Directory?</p>
                    <p className="text-sm text-slate-400 leading-relaxed font-medium">This action is irreversible and will immediately revoke all system access for this individual.</p>
                </div>
            </Modal>

            {/* Invite Modal */}
            <Modal
                isOpen={!!userToInvite}
                onClose={() => setUserToInvite(null)}
                title="Send Account Invitation"
                footer={(
                    <>
                        <button
                            onClick={confirmInvite}
                            disabled={sendingInvite}
                            className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-lg shadow-indigo-900/20 hover:bg-indigo-700 transition-all disabled:opacity-50"
                        >
                            {sendingInvite ? 'Sending...' : 'Send Invitation'}
                        </button>
                        <button
                            onClick={() => setUserToInvite(null)}
                            className="text-[#18216D] px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-slate-50 transition-all"
                        >
                            Cancel
                        </button>
                    </>
                )}
            >
                <div className="space-y-4">
                    <p>Send an account setup invitation to <span className="text-[#18216D] font-black underline underline-offset-4 decoration-indigo-500/30">{userToInvite?.name}</span>?</p>
                    <p className="text-sm text-slate-400 leading-relaxed font-medium">This will send an email identical to the welcome email, but featuring a secure link for the parent to set up their password, which automatically activates their account.</p>
                </div>
            </Modal>
        </div>
    );
};

export default UserList;
